import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { AppUI } from '@seedlang/state';
import { AuthenticateStore } from '@seedlang/stores';
import Link from 'components/link';
import Modal from 'components/modal';
import Button from 'components/button/button';
import { Theme } from '@seedlang/constants';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  padding: 10px;
  position: relative;
  text-align: center;
`;

const EmailIcon = styled.div`
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #d9f0f4;
  color: #14788b;
  font-size: 28px;
`;

const Copy = styled.div`
  text-align: center;
  width: 100%;
  min-width: 0;
  color: #24515b;
  font-size: 14px;
  line-height: 22px;
  overflow-wrap: anywhere;

`;

const ErrorMessage = styled.div`
  color: #a22929;
  margin-top: 4px;
`;

const EditEmailLink = styled(Link)`
  display: block;
  width: fit-content;
  margin: 0 auto;
  color: #126f80;
  font-size: 12px;
  text-decoration: underline;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

@observer
class EmailConfirmationReminder extends React.Component {
  @observable dismissedUserId = null;
  @observable sending = false;
  @observable sentTo = null;
  @observable error = null;

  sendEmail = async () => {
    if (this.sending) return;
    this.sending = true;
    this.error = null;
    const email = AppUI.user.email;
    try {
      const response = await AuthenticateStore.sendConfirmationInstructions();
      this.sentTo = response.email || email;
    } catch (error) {
      this.error = error.message || 'We could not send the email. Please try again.';
    } finally {
      this.sending = false;
    }
  };

  dismiss = () => {
    this.dismissedUserId = AppUI.user.id;
    AppUI.designateUiElementAsSeen('email-confirmation-reminder');
  };

  render() {
    if (AppUI.routeStore.routeName?.startsWith('onboarding')) return null;
    if (this.dismissedUserId === AppUI.user.id || AppUI.siteIsMemrev || !AppUI.userIsSignedInAndNotGuest || AppUI.user.confirmed ||
        !AppUI.user.showEmailConfirmationReminder || AppUI.wasUiElementSeen('email-confirmation-reminder')) return null;

    return (
      <Modal
        width='100%'
        maxWidth='560px'
        title={this.sentTo ? 'Check your inbox' : 'Please confirm your email'}
        background={Theme.blue}
        onCloseModal={this.dismiss}
      >
        <Wrapper role='dialog' aria-modal='true' aria-label='Email confirmation reminder' onKeyDown={event => { if (event.key === 'Escape') this.dismiss(); }}>
          <EmailIcon aria-hidden='true'><i className={this.sentTo ? 'fa fa-check' : 'fa fa-envelope-o'} /></EmailIcon>
          <Copy role={this.sentTo ? 'status' : undefined}>
            We’ve sent a confirmation email to <strong>{this.sentTo || AppUI.user.email}</strong>. Please click the link in that email to confirm your email address.
            {this.error && <ErrorMessage role='alert'>{this.error}</ErrorMessage>}
          </Copy>
          <Actions>
            {!this.sentTo &&
              <Button onClick={this.sendEmail} disabled={this.sending} background={Theme.blue} width='auto' style={{ alignSelf: 'center' }}>
                {this.sending ? 'Sending…' : 'Resend confirmation email'}
              </Button>
            }
            <EditEmailLink to='settings.user_profile' onClick={this.dismiss}>Edit my email address</EditEmailLink>
          </Actions>
        </Wrapper>
      </Modal>
    );
  }
}

export default EmailConfirmationReminder;
